<script setup>
import { useGtag } from "vue-gtag-next";
import { highlightText } from "@/lib/utils.js";

import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/all";
gsap.registerPlugin(ScrollToPlugin);

import { scrollToTop } from "@/lib/gsap-core";

const emitter = useEmitter();
const nuxtApp = useNuxtApp();

const { locale } = useI18n();
const { setMeta } = useMeta();
const { fetchSearchResult, fetchPage } = useFetchAPI();
const { stopLoading } = useAppState();
const { event } = useGtag();

const searchData = computed(() => {
    return (
        nuxtApp.payload.data?.["fetchPage-search"]?.[locale.value] ||
        nuxtApp.payload.data?.["fetchPage-search"]?.zh
    );
});

if (!searchData.value) await fetchPage("search");

const searchInput = ref(null);
const { searchKeywords, setKeywords } = useSearchKeywords();
const keyword = computed({
    get() {
        return searchKeywords.value;
    },
    set(val) {
        setKeywords(val);
    },
});

// 所有搜索結果
const searchResults = ref([]);
// 目前頁碼
const currentPage = ref(1);
// 每頁顯示的結果數量
const itemsPerPage = 10;
// 總頁數
const totalPages = computed(() =>
    Math.ceil(searchResults.value.length / itemsPerPage),
);
// 顯示的結果
const displayedResults = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return searchResults.value.slice(start, end);
});

// 加載中狀態
const isLoading = ref(false);

// 無搜索結果時顯示
const noResultsVisible = ref(false);

// 執行搜索
const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) return;

    isLoading.value = true;
    event("search", {
        search_term: searchTerm.trim(),
        event_category: "all site",
        event_label: "search",
    });

    try {
        const { data } = await fetchSearchResult(searchTerm.trim());
        searchResults.value = data.value.processedResult.filter(
            (item) => item !== null,
        );
        noResultsVisible.value = searchResults.value.length === 0;
    } catch (error) {
        searchResults.value = [];
        noResultsVisible.value = true;
    } finally {
        isLoading.value = false;
    }
};

const cancelSearch = () => {
    keyword.value = "";
    noResultsVisible.value = false;
    searchResults.value = [];
    nextTick(() => {
        searchInput.value?.focus();
    });
};

const isComposing = ref(false);

const search = () => {
    if (keyword.value && !isComposing.value) {
        performSearch(keyword.value);
    }
};

const handleKeyDown = (event) => {
    if (event.key === "Enter" && !isComposing.value) {
        search();
    }
};

// 分頁事件處理
const handlePageChange = (page) => {
    // 換頁後回到頁面頂端
    currentPage.value = page;
    scrollToTop();
};

onMounted(() => {
    search();

    emitter.on("perform-search", (searchTerm) => {
        performSearch(searchTerm);
    });

    watchEffect(() => {
        if (!isLoading.value) {
            stopLoading();
        }
    });
});

onUnmounted(() => {
    emitter.off("perform-search");
});

await setMeta(searchData.value);
</script>

<template>
    <div id="search">
        <div class="slt-container">
            <div v-if="searchResults.length > 0" class="flex justify-center">
                <div
                    class="w-full pb-[calc(var(--window-height)*0.1214)] pt-[calc(var(--window-height)*0.091+14.25vw)] sm:w-[auto] sm:pb-[calc(var(--window-height)*0.123)] sm:pt-[calc(var(--window-height)*0.0508+8.33vw)] md:w-[90%] md:max-w-[1032px] lg:pb-40 lg:pt-[calc(4.23vw+68px)] 3xl:pt-[150px]"
                >
                    <div
                        class="flex flex-col gap-2 px-4 sm:flex-row sm:items-end sm:justify-between sm:px-10 md:px-0"
                    >
                        <div
                            class="flex items-center gap-[3.05vw] rounded-[18.32vw] bg-base-silver px-[5.09vw] py-[4.07vw] xs:gap-3 xs:rounded-[72px] xs:px-5 xs:py-4 sm:w-[60vw] sm:max-w-[462px]"
                        >
                            <SvgSearch24
                                class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                                :color="'black'"
                                @click="search"
                                v-sendGAsignal="{
                                    event_category: 'search',
                                    event_label: 'search',
                                }"
                            />
                            <input
                                type="text"
                                :placeholder="searchData?.meta_box?.placeholder"
                                class="slt-placeholder w-full flex-1 bg-transparent text-base-black placeholder:text-base-black-40"
                                @keydown="handleKeyDown"
                                @compositionstart="isComposing = true"
                                @compositionend="isComposing = false"
                                v-model="keyword"
                                ref="searchInput"
                            />
                            <SvgClose24
                                v-show="keyword"
                                class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                                :color="'#FF6400'"
                                @click="cancelSearch"
                                v-sendGAsignal="{
                                    event_category: 'search',
                                    event_label: 'cancel search',
                                }"
                            />
                        </div>
                        <p
                            class="slt-body-1 px-[2.03vw] text-base-black-60 xs:px-2"
                        >
                            找到
                            <span>{{ searchResults.length }}</span> 項結果
                        </p>
                    </div>
                    <!-- 加載中提示 -->
                    <div
                        v-if="isLoading"
                        class="mt-20 flex justify-center sm:mt-40"
                    >
                        <div class="slt-bouncing w-[40px]">
                            <GamaGlobeLottie />
                        </div>
                    </div>
                    <!-- 搜索結果 -->
                    <section v-else>
                        <div
                            class="slt-flex-center mt-8 flex-col gap-6 px-4 sm:mt-10 sm:gap-10 sm:px-10 md:mx-auto md:max-w-[1032px] md:gap-12 md:px-[unset]"
                        >
                            <ul class="slt-flex-center w-full flex-col gap-6">
                                <li
                                    v-for="result in displayedResults"
                                    :key="result.id"
                                    class="w-full"
                                >
                                    <!-- 搜索結果項 -->
                                    <NuxtLink
                                        :to="$localePath(result.url)"
                                        class="border-news flex flex-row items-start justify-start overflow-hidden rounded-[var(--card-radius)] bg-base-white text-base-black shadow-custom [--card-radius:1rem]"
                                        v-sendGAsignal="{
                                            event_category: 'search',
                                            event_label: result.title,
                                        }"
                                    >
                                        <div
                                            class="relative h-[calc(160px*.8875)] w-40 flex-shrink-0 overflow-hidden rounded-[var(--card-radius)] sm:h-[calc(264px*.8875)] sm:w-[264px] lg:h-[calc(280px*.8875)] lg:w-[280px]"
                                        >
                                            <img
                                                :src="result.thumbnail"
                                                :alt="result.title"
                                                class="absolute inset-0 h-full w-full object-cover"
                                            />
                                        </div>
                                        <div
                                            class="relative flex-1 self-stretch p-4 pb-5 sm:px-6 sm:pb-10 sm:pt-6 lg:px-8 lg:py-8"
                                        >
                                            <div
                                                class="flex items-center"
                                                :class="{
                                                    'justify-between':
                                                        result.category,
                                                    'justify-end':
                                                        !result.category,
                                                }"
                                            >
                                                <AtomsTag
                                                    v-if="result.category"
                                                    :html="result.category"
                                                ></AtomsTag>
                                                <AtomsBody1
                                                    lang="en"
                                                    class="hidden text-base-black-60 sm:block"
                                                    >{{
                                                        result.date
                                                    }}</AtomsBody1
                                                >
                                            </div>
                                            <div
                                                class="mt-4 flex flex-col items-start justify-center sm:gap-6"
                                            >
                                                <AtomsH4
                                                    class="line-clamp-3 sm:line-clamp-2"
                                                    v-html="
                                                        highlightText(
                                                            result.title,
                                                            keyword,
                                                        )
                                                    "
                                                ></AtomsH4>
                                                <p
                                                    class="sm:display-[unset] hidden text-base-black-60 sm:line-clamp-2"
                                                    v-html="
                                                        highlightText(
                                                            result.content,
                                                            keyword,
                                                        )
                                                    "
                                                ></p>
                                            </div>
                                        </div>
                                    </NuxtLink>
                                </li>
                            </ul>
                        </div>
                    </section>
                    <!-- 分頁組件 -->
                    <section v-if="!isLoading && totalPages > 1">
                        <MoleculesPagination
                            class="!mt-12 md:!mt-20"
                            :current-page="currentPage"
                            :total-pages="totalPages"
                            :items-per-page="itemsPerPage"
                            @update:current-page="handlePageChange"
                        />
                    </section>
                </div>
            </div>
            <!-- 無搜索結果時顯示 -->
            <div
                v-else-if="!isLoading && noResultsVisible"
                class="flex justify-center"
            >
                <div
                    class="relative flex w-full flex-col gap-[7.63vw] px-[6.1vw] pb-[calc(var(--window-height)*0.1214)] pt-[calc(var(--window-height)*0.091+14.25vw)] xs:gap-[30px] xs:px-[24px] sm:px-[5.21vw] sm:pb-[calc(var(--window-height)*0.1484)] sm:pt-[calc(var(--window-height)*0.1289+8.33vw)] lg:max-w-[952px] lg:justify-center lg:pb-[calc(var(--window-height)*0.25)] lg:pt-[calc(4.23vw+0.128*var(--window-height))] 3xl:pt-[calc(82px+0.128*var(--window-height))]"
                >
                    <h2
                        class="slt-h3-ttd flex flex-col gap-2 px-[2.03vw] xs:px-2"
                    >
                        <span
                            v-for="title in searchData?.meta_box?.notFound"
                            :key="title"
                            class="block"
                            >{{ title }}</span
                        >
                    </h2>
                    <div
                        class="flex items-center gap-[3.05vw] rounded-[18.32vw] bg-base-silver px-[5.09vw] py-[4.07vw] xs:gap-3 xs:rounded-[72px] xs:px-5 xs:py-4 lg:max-w-[464px]"
                    >
                        <SvgSearch24
                            class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                            :color="'black'"
                            @click="search"
                            v-sendGAsignal="{
                                event_category: 'search',
                                event_label: 'search',
                            }"
                        />
                        <input
                            type="text"
                            :placeholder="searchData?.meta_box?.placeholder"
                            class="slt-placeholder w-full flex-1 bg-transparent text-base-black placeholder:text-base-black-40"
                            @keydown="handleKeyDown"
                            @compositionstart="isComposing = true"
                            @compositionend="isComposing = false"
                            v-model="keyword"
                            ref="searchInput"
                        />
                        <SvgClose24
                            v-show="keyword"
                            class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                            :color="'#FF6400'"
                            @click="cancelSearch"
                            v-sendGAsignal="{
                                event_category: 'search',
                                event_label: 'cancel search',
                            }"
                        />
                    </div>
                    <div class="px-[2.03vw] xs:px-2">
                        <h5 class="slt-h5 mb-[5.09vw] xs:mb-5">
                            <span
                                v-for="title in searchData?.meta_box?.fastLinks
                                    .title"
                                :key="title"
                                class="block"
                                >{{ title }}</span
                            >
                        </h5>
                        <ul
                            class="slt-body-1 -ml-4 flex flex-col gap-[3.05vw] xs:gap-3"
                        >
                            <li
                                v-for="data in searchData?.meta_box?.fastLinks
                                    .data"
                                :key="data"
                            >
                                <NuxtLink
                                    class="btn flex w-fit items-center gap-[3.05vw] rounded-full py-1 pl-4 pr-5 transition-all duration-300 ease-in-out xs:gap-3 sm:hover:bg-base-white"
                                    :to="$localePath(data.link)"
                                    :external="data.type === 'external'"
                                    :target="
                                        data.type === 'external'
                                            ? '_blank'
                                            : '_self'
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'search',
                                        event_label: data.name.join(''),
                                    }"
                                >
                                    <div class="btn__arrow__group">
                                        <SvgArrowForward20
                                            :color="'black'"
                                            class="btn__arrow btn__arrow--main"
                                        />
                                        <SvgArrowForward20
                                            :color="'black'"
                                            class="btn__arrow btn__arrow--hover"
                                        />
                                    </div>
                                    <span
                                        v-for="text in data.name"
                                        :key="text"
                                        class="block"
                                        >{{ text }}</span
                                    >
                                </NuxtLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <!-- 初始狀態或加載中 -->
            <div v-else-if="isLoading" class="flex h-[70vh] justify-center">
                <div class="flex items-center justify-center">
                    <div class="w-[60px] animate-bounce">
                        <GamaGlobeLottie />
                    </div>
                </div>
            </div>
            <!-- 初始狀態 -->
            <div v-else class="flex justify-center">
                <div
                    class="relative flex w-full flex-col gap-[7.63vw] px-[6.1vw] pb-[calc(var(--window-height)*0.1214)] pt-[calc(var(--window-height)*0.091+14.25vw)] xs:gap-[30px] xs:px-[24px] sm:px-[5.21vw] sm:pb-[calc(var(--window-height)*0.1484)] sm:pt-[calc(var(--window-height)*0.1289+8.33vw)] lg:max-w-[952px] lg:justify-center lg:pb-[calc(var(--window-height)*0.25)] lg:pt-[calc(4.23vw+0.128*var(--window-height))] 3xl:pt-[calc(82px+0.128*var(--window-height))]"
                >
                    <h2
                        class="slt-h3-ttd flex flex-col gap-2 px-[2.03vw] xs:px-2"
                    >
                        <span
                            v-for="title in searchData?.meta_box?.title"
                            :key="title"
                            class="block"
                            >{{ title }}</span
                        >
                    </h2>
                    <div
                        class="flex items-center gap-[3.05vw] rounded-[18.32vw] bg-base-silver px-[5.09vw] py-[4.07vw] xs:gap-3 xs:rounded-[72px] xs:px-5 xs:py-4 lg:max-w-[464px]"
                    >
                        <SvgSearch24
                            class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                            :color="'black'"
                            @click="search"
                            v-sendGAsignal="{
                                event_category: 'search',
                                event_label: 'search',
                            }"
                        />
                        <input
                            type="text"
                            :placeholder="searchData?.meta_box?.placeholder"
                            class="slt-placeholder w-full flex-1 bg-transparent text-base-black placeholder:text-base-black-40"
                            @keydown="handleKeyDown"
                            @compositionstart="isComposing = true"
                            @compositionend="isComposing = false"
                            v-model="keyword"
                            ref="searchInput"
                        />
                        <SvgClose24
                            v-show="keyword"
                            class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                            :color="'#FF6400'"
                            @click="cancelSearch"
                            v-sendGAsignal="{
                                event_category: 'search',
                                event_label: 'cancel search',
                            }"
                        />
                    </div>
                    <div class="px-[2.03vw] xs:px-2">
                        <h5 class="slt-h5 mb-[5.09vw] xs:mb-5">
                            <span
                                v-for="title in searchData?.meta_box?.fastLinks
                                    .title"
                                :key="title"
                                class="block"
                                >{{ title }}</span
                            >
                        </h5>
                        <ul
                            class="slt-body-1 -ml-4 flex flex-col gap-[3.05vw] xs:gap-3"
                        >
                            <li
                                v-for="data in searchData?.meta_box?.fastLinks
                                    .data"
                                :key="data"
                            >
                                <NuxtLink
                                    class="btn flex w-fit items-center gap-[3.05vw] rounded-full py-1 pl-4 pr-5 transition-all duration-300 ease-in-out xs:gap-3 sm:hover:bg-base-white"
                                    :to="$localePath(data.link)"
                                    :external="data.type === 'external'"
                                    :target="
                                        data.type === 'external'
                                            ? '_blank'
                                            : '_self'
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'search',
                                        event_label: data.name.join(''),
                                    }"
                                >
                                    <div class="btn__arrow__group">
                                        <SvgArrowForward20
                                            :color="'black'"
                                            class="btn__arrow btn__arrow--main"
                                        />
                                        <SvgArrowForward20
                                            :color="'black'"
                                            class="btn__arrow btn__arrow--hover"
                                        />
                                    </div>
                                    <span
                                        v-for="text in data.name"
                                        :key="text"
                                        class="block"
                                        >{{ text }}</span
                                    >
                                </NuxtLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
/* button arrow animation */
.btn__arrow__group {
    @apply relative inline-block h-[5.09vw] w-[5.09vw] overflow-hidden align-middle xs:h-[20px] xs:w-[20px];
}
.btn__arrow {
    @apply absolute right-0 top-0 size-full;
    transition:
        transform 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33),
        opacity 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33);
}
.btn__arrow--hover {
    transform: translate3d(-100%, 0, 0);
    opacity: 0;
}

@media (min-width: 640px) {
    .btn:hover .btn__arrow--main {
        transform: translate3d(100%, 0, 0);
        opacity: 0;
    }
    .btn:hover .btn__arrow--hover {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}
</style>
